import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Award, Download, GraduationCap, BadgeCheck, Sparkles, Mail } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, EmptyState } from "../components/shared";
import { Button } from "@/components/ui/button";

export default function Profile() {
  const { user } = useAuth();
  const { data: courses, isLoading } = useQuery({ queryKey: ["courses"], queryFn: async () => (await api.get("/academy/courses")).data });
  const { data: summary } = useQuery({ queryKey: ["academy-summary"], queryFn: async () => (await api.get("/academy/summary")).data });

  if (isLoading) return <Loading />;
  const completed = (courses || []).filter((c) => c.status === "completed");
  const initials = (user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");

  return (
    <div>
      <PageHeader eyebrow="Adviser Profile" title="Your Certificate Wall"
        description="Your professional standing at a glance — specializations, credentials, and every CPD certificate you've earned." />

      {/* Identity + CPD */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-900 text-white flex items-center justify-center text-xl font-display font-bold">{initials}</div>
            <div>
              <div className="font-display text-xl font-bold text-slate-900">{user?.name}</div>
              <div className="text-sm text-slate-500 capitalize">{(user?.role || "").replace("_", " ")}</div>
              {user?.email && <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {user.email}</div>}
            </div>
          </div>
          {user?.bio && <p className="text-sm text-slate-600 mt-4 leading-relaxed">{user.bio}</p>}
          {(user?.specializations || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-mono uppercase text-slate-400 mb-2">Specializations</div>
              <div className="flex flex-wrap gap-2">
                {user.specializations.map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{s}</span>)}
              </div>
            </div>
          )}
          {(user?.certifications || []).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-mono uppercase text-slate-400 mb-2">Credentials</div>
              <div className="flex flex-wrap gap-2">
                {user.certifications.map((c) => <span key={c} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200 flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-emerald-600" /> {c}</span>)}
              </div>
            </div>
          )}
        </div>
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 text-white rounded-2xl shadow-sm p-6 flex flex-col justify-center">
          <Award className="w-7 h-7 mb-3 opacity-90" />
          <div className="font-display text-4xl font-bold">{summary?.cpd_credits ?? 0}</div>
          <div className="text-sm opacity-90">CPD credits earned</div>
          <div className="text-xs opacity-75 mt-2">{summary?.completed ?? 0} courses completed · {summary?.in_progress ?? 0} in progress</div>
        </div>
      </div>

      {/* Certificate wall */}
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-display text-xl font-bold text-slate-900">Certificates</h2>
        <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{completed.length}</span>
      </div>
      {completed.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No certificates yet" description="Complete a CPD course in the Adviser Academy to earn your first certificate."
          action={<Button onClick={() => (window.location.href = "/academy")} className="bg-emerald-600 hover:bg-emerald-700">Go to Academy</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {completed.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid={`wall-cert-${c.id}`} className="relative bg-white rounded-2xl border border-emerald-200/70 shadow-sm p-6 overflow-hidden">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-emerald-50" />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <Award className="w-8 h-8 text-emerald-600" />
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-emerald-600 text-white">+{c.cpd_credits} CPD</span>
                </div>
                <div className="font-display font-semibold text-slate-900 mt-3 leading-snug">{c.title}</div>
                <div className="text-xs text-slate-400 mt-1">{c.category} · {c.level}</div>
                <a href={`${API}/academy/courses/${c.id}/certificate`} target="_blank" rel="noreferrer" data-testid={`wall-download-${c.id}`}>
                  <Button size="sm" variant="outline" className="mt-4 w-full"><Download className="w-4 h-4 mr-1.5" /> Download certificate</Button>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
